import React, { useMemo, useState } from "react";
import "./create-invoice.style.scss";
import Swal from "sweetalert2";

// components
import InvoiceForm from "../../components/invoice-form/invoice-form.component";
import DraftList from "../../components/draft-list/draft-list.component";
import ReadyDraftList from "../../components/draft-list/ready-draft-list.component";
import type { DraftListEntry } from "../../components/draft-list/draft-item.component";
import FormError from "../../components/form-error/form-error.component";
import InlineNotice from "../../components/inline-notice/inline-notice.component";

import { useLocalDrafts } from "../../context/local-drafts.context";

// queries
import { useSaveDraft, useUpdateDraft } from "../../queries/drafts.queries";
import type { Draft, DraftPayload } from "../../queries/drafts.queries";
import { useBillDates } from "../../queries/bill.queries";
import { useActiveExternalApp } from "../../queries/company.queries";
import type { InvoiceFormValues } from "../../components/invoice-form/invoice-form.types";
import { sanitizeDraftPayload } from "../../components/invoice-form/invoice-form.util";

// global functions
import { fromAdToBs, isEmptyObject } from "../../global/function";

// for date purpose
import moment from "moment";

type DraftFormValues = Partial<InvoiceFormValues> & Record<string, unknown>;

export default function CreateInvoice() {
	const { localDraftsList, setLocalDraftsList, updateLocalDraft } =
		useLocalDrafts();
	const { data: billDates } = useBillDates();
	const lastBillDate = billDates?.lastInvoiceDate ?? "";
	const { billModel, systemType } = useActiveExternalApp();
	const isCreationBlocked = billModel === "A";
	const connectedAppName = systemType
		? systemType.toUpperCase()
		: "the connected app";
	const [currentDraft, setCurrentDraft] = useState<DraftFormValues>({});
	const [viewVisibleList, setViewVisibleList] = useState<"draft" | "ready">(
		"draft",
	);

	// stores invoice number of draft that has been changed
	const [unsavedChanges, setUnsavedChanges] = useState<
		Partial<InvoiceFormValues> | Record<string, never>
	>({});

	const saveDraftMutation = useSaveDraft();
	const updateDraftMutation = useUpdateDraft();

	// Replaces useSaveDraft/useUpdateDraft's former toast for handleSaveDraft
	// (this page has no modal to show a result inside, unlike the profile
	// side-modals — an inline flash above the form fits better than a toast).
	const [saveNotice, setSaveNotice] = useState<string | null>(null);

	// choose draft object for edit or add
	const handleDraftSelection = async (draft: DraftListEntry) => {
		if (!isEmptyObject(currentDraft) && !isEmptyObject(unsavedChanges)) {
			// Same sanitizer invoice-form's own Save/Ready/Create Bill submit
			// uses (invoice-form.util.ts) — unsavedChanges is always a full
			// InvoiceFormValues snapshot (every onUnsavedChanges call site
			// spreads ...formik.values before overriding the changed field),
			// so it's safe to run through the same payload builder rather than
			// sending raw, unconverted formik values straight to the BE.
			const sanitizedDraft = sanitizeDraftPayload(
				currentDraft,
				unsavedChanges as InvoiceFormValues,
			);

			// if the changes have been done in drafts from DB
			if (
				currentDraft?.draft_bill_status === "draft" &&
				currentDraft?.invoice_number === unsavedChanges?.invoice_number
			) {
				Swal.fire({
					title: "Save Changes?",
					text: "Do you want to save the changes?",
					showDenyButton: true,
					confirmButtonText: "Save",
					denyButtonText: "Cancel",
					customClass: {
						popup: "alert-modal",
						actions: "btn-container",
						confirmButton: "alert-btn btn-confirm",
						denyButton: "alert-btn btn-cancel",
					},
				}).then(async (result) => {
					/* Read more about isConfirmed, isDenied below */
					if (result.isConfirmed) {
						try {
							await updateDraftMutation.mutateAsync({
								drafts: [sanitizedDraft as Partial<Draft>],
								draft_bill_status: "draft",
							});
							setUnsavedChanges({});
							Swal.fire({
								title: "Draft changes saved!",
								icon: "success",
								customClass: {
									popup: "alert-modal",
									actions: "btn-container",
									confirmButton: "alert-btn btn-confirm",
								},
							}).then(
								(result) =>
									result.isConfirmed &&
									setCurrentDraft(draft),
							);
						} catch (error) {
							// Stay on the currently-edited draft; surface the
							// failure the same way as the adjacent
							// success/cancel outcomes in this flow (Swal),
							// not a toast.
							Swal.fire({
								title: "Failed to save changes",
								text:
									error instanceof Error
										? error.message
										: "Something went wrong. Please try again.",
								icon: "error",
								customClass: {
									popup: "alert-modal",
									actions: "btn-container",
									confirmButton: "alert-btn btn-confirm",
								},
							});
							return;
						}
					} else if (result.isDenied) {
						setUnsavedChanges({});
						Swal.fire({
							title: "Changes are not saved",
							icon: "info",
							customClass: {
								popup: "alert-modal",
								actions: "btn-container",
								confirmButton: "alert-btn btn-confirm",
								denyButton: "alert-btn btn-cancel",
							},
						}).then(
							(result) =>
								result.isConfirmed && setCurrentDraft(draft),
						);
					}
				});
				return;
			} else if (
				currentDraft?.invoice_number === unsavedChanges?.invoice_number
			) {
				// if changes have been done in drafts stored in redux, simply update redux
				let localUpdate = false;
				const isBuyerNameNotEmpty =
					!!unsavedChanges?.buyer_name &&
					unsavedChanges?.buyer_name.trim() !== "";
				const isBilledItemsNotEmpty =
					Array.isArray(unsavedChanges?.billed_items) &&
					unsavedChanges?.billed_items?.length > 0;

				if (isBuyerNameNotEmpty) {
					localUpdate = true;
				} else {
					if (isBilledItemsNotEmpty) {
						const areBilledItemsFieldsNotEmpty =
							unsavedChanges?.billed_items?.every(
								(item) =>
									item.item_name &&
									item.item_name.trim() !== "" &&
									item.item_price !== undefined &&
									Number(item.item_price) > 0 &&
									item.item_qty !== undefined &&
									Number(item.item_qty) > 0,
							);
						localUpdate = !!areBilledItemsFieldsNotEmpty;
					}
				}
				localUpdate && updateLocalDraft(sanitizedDraft);
				setCurrentDraft(draft);
				setUnsavedChanges({});
			} else {
				setCurrentDraft(draft);
				setUnsavedChanges({});
			}
		} else {
			setCurrentDraft(draft);
		}
	};

	// individual draft save / update (draft and ready status) [handle currentDraft]
	const handleSaveDraft = async (
		draftData: DraftFormValues,
		saveTo: string,
	) => {
		let saveSucceeded = false;

		if (saveTo === "draft") {
			if (draftData?.draft_bill_status === "draft") {
				// to show updated draft data in the form
				setCurrentDraft(draftData);
				try {
					await updateDraftMutation.mutateAsync({
						drafts: [draftData as Partial<Draft>],
						draft_bill_status: "draft",
					});
					setSaveNotice("Draft saved");
				} catch {
					// FormError (bound to updateDraftMutation) renders the message
				}
			} else {
				try {
					await saveDraftMutation.mutateAsync({
						drafts: [draftData as DraftPayload],
						draft_bill_status: "draft",
					});
					saveSucceeded = true;
					setSaveNotice("Draft saved");
				} catch {
					// FormError (bound to saveDraftMutation) renders the message
				}
				// reset current draft since data changes after draft creation
				setCurrentDraft({});
			}
		} else {
			if (draftData?.draft_bill_status === "draft") {
				try {
					await updateDraftMutation.mutateAsync({
						drafts: [draftData as Partial<Draft>],
						draft_bill_status: "ready",
					});
					setSaveNotice("Draft marked ready");
					// only show updated draft data in the form if failed
					setCurrentDraft({});
				} catch {
					setCurrentDraft(draftData);
				}
			} else {
				try {
					await saveDraftMutation.mutateAsync({
						drafts: [draftData as DraftPayload],
						draft_bill_status: "ready",
					});
					saveSucceeded = true;
					setSaveNotice("Draft marked ready");
					// only show updated draft data in the form if failed
					setCurrentDraft({});
				} catch {
					setCurrentDraft(draftData);
				}
			}
		}

		if (saveSucceeded) {
			// remove drafts from local drafts
			const newLocalDrafts = localDraftsList.filter(
				(localDraft: DraftListEntry) =>
					localDraft.invoice_number !== draftData.invoice_number,
			);
			setLocalDraftsList(newLocalDrafts);
		} else {
			// save updated draft(local drafts) — will rerender the form
			updateLocalDraft(draftData);
		}
	};

	// date show
	const last_bill_date = useMemo(
		() =>
			lastBillDate !== "" &&
			`${fromAdToBs(lastBillDate, "DD MMMM, YYYY")} | ${moment(
				lastBillDate,
			).format("DD MMMM YYYY")}`,
		[lastBillDate],
	);

	if (isCreationBlocked) {
		return (
			<div className="create-invoice-wrapper">
				<div className="draft-form-container">
					<div className="draft-form-header">
						<div className="section-title-text">
							Create New Invoice
						</div>
					</div>
					<div className="header-divider"></div>
					<InlineNotice
						message={`Bill creation is disabled — ${connectedAppName} manages billing for this company. Bills pushed from ${connectedAppName} will appear in your bill list automatically.`}
						type="warning"
					/>
				</div>
			</div>
		);
	}

	return (
		<div className="create-invoice-wrapper">
			<DraftList
				currentDraft={currentDraft}
				onSetCurrent={handleDraftSelection}
				unsavedInvoiceNumber={unsavedChanges?.invoice_number || ""}
				viewVisibleList={viewVisibleList}
				setViewVisibleList={() => setViewVisibleList("draft")}
			/>
			<div className="draft-form-container">
				<div className="draft-form-container-inner">
					<div className="draft-form-header">
						<div className="section-title-text">
							Create New Invoice
						</div>
						{lastBillDate !== "" && (
							<div className="form-header-subtext">
								<div className="label-info">
									{last_bill_date}
								</div>
								<div className="label">Last Bill Date</div>
							</div>
						)}
					</div>
					<div className="header-divider"></div>
					<FormError
						isError={
							saveDraftMutation.isError ||
							updateDraftMutation.isError
						}
						error={
							saveDraftMutation.error || updateDraftMutation.error
						}
						fallback="Something went wrong. Please try again."
					/>
					<InlineNotice
						message={saveNotice}
						type="success"
						autoHideMs={3000}
						onDismiss={() => setSaveNotice(null)}
					/>
					<InvoiceForm
						formType="bulk"
						draftSave={handleSaveDraft}
						currentDraft={currentDraft}
						onSetCurrent={handleDraftSelection}
						onUnsavedChanges={setUnsavedChanges}
					/>
				</div>
			</div>
			<ReadyDraftList
				recentBillDate={last_bill_date}
				viewVisibleList={viewVisibleList}
				setViewVisibleList={() => setViewVisibleList("ready")}
			/>
		</div>
	);
}
